import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  Flame, 
  Droplets, 
  Settings, 
  Phone, 
  Mail, 
  MapPin,
  Menu,
  X,
  ChevronRight,
  Clock,
  Truck,
  Shield
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_96c18085-1adb-46e6-8110-6a2f5206b8df/artifacts/z4ny02kg_WhatsApp_Image_2026-03-19_at_12.44.35-removebg-preview.png";

const LandingPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="GasMan4U" className="h-10 w-auto" />
              <span className="text-xl font-bold text-gray-900">GasMan4U</span>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-8">
              <button 
                onClick={() => scrollToSection("hero")} 
                className="text-gray-600 hover:text-gray-900 transition-colors font-medium"
                data-testid="nav-home"
              >
                Home
              </button>
              <button 
                onClick={() => scrollToSection("services")} 
                className="text-gray-600 hover:text-gray-900 transition-colors font-medium"
                data-testid="nav-services"
              >
                Services
              </button>
              <button 
                onClick={() => scrollToSection("about")} 
                className="text-gray-600 hover:text-gray-900 transition-colors font-medium"
                data-testid="nav-about"
              >
                About
              </button>
              <button 
                onClick={() => scrollToSection("contact")} 
                className="text-gray-600 hover:text-gray-900 transition-colors font-medium"
                data-testid="nav-contact"
              >
                Contact
              </button>
              <Link to="/admin/login">
                <Button 
                  className="bg-[#6BB5E0] text-white hover:bg-[#38BDF8] rounded-full px-6 glow-primary"
                  data-testid="nav-admin-login"
                >
                  Admin Login
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden text-gray-900"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200">
            <div className="px-4 py-4 space-y-3">
              <button 
                onClick={() => scrollToSection("hero")} 
                className="block w-full text-left text-gray-600 hover:text-gray-900 py-2"
              >
                Home
              </button>
              <button 
                onClick={() => scrollToSection("services")} 
                className="block w-full text-left text-gray-600 hover:text-gray-900 py-2"
              >
                Services
              </button>
              <button 
                onClick={() => scrollToSection("about")} 
                className="block w-full text-left text-gray-600 hover:text-gray-900 py-2"
              >
                About
              </button>
              <button 
                onClick={() => scrollToSection("contact")} 
                className="block w-full text-left text-gray-600 hover:text-gray-900 py-2"
              >
                Contact
              </button>
              <Link to="/admin/login" className="block">
                <Button className="w-full bg-[#6BB5E0] text-white hover:bg-[#38BDF8] rounded-full">
                  Admin Login
                </Button>
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="hero" className="hero-section pt-24 pb-16 md:pt-32 md:pb-24" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                Fast & Reliable
                <span className="text-[#0284c7]"> Gas Delivery</span>
                <br />at Your Doorstep
              </h1>
              <p className="text-gray-600 text-lg mb-8 max-w-lg">
                Your trusted partner for gas cylinders, water cans, regulators, and all gas-related products. 
                Quick delivery, quality assured.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  onClick={() => scrollToSection("contact")}
                  className="bg-[#6BB5E0] text-white hover:bg-[#38BDF8] rounded-full px-8 py-6 text-lg font-semibold glow-primary"
                  data-testid="hero-cta-primary"
                >
                  Order Now
                  <ChevronRight className="ml-2" size={20} />
                </Button>
                <Button 
                  onClick={() => scrollToSection("services")}
                  variant="outline"
                  className="border-[#6BB5E0] text-[#0284c7] hover:bg-[#6BB5E0]/10 rounded-full px-8 py-6 text-lg"
                  data-testid="hero-cta-secondary"
                >
                  Our Services
                </Button>
              </div>

              {/* Stats */}
              <div className="flex flex-wrap gap-8 mt-12">
                <div className="animate-fade-in-delay-1">
                  <p className="text-3xl font-bold text-[#0284c7]">500+</p>
                  <p className="text-gray-500 text-sm">Happy Customers</p>
                </div>
                <div className="animate-fade-in-delay-2">
                  <p className="text-3xl font-bold text-[#0284c7]">24/7</p>
                  <p className="text-gray-500 text-sm">Support Available</p>
                </div>
                <div className="animate-fade-in-delay-3">
                  <p className="text-3xl font-bold text-[#0284c7]">30min</p>
                  <p className="text-gray-500 text-sm">Avg. Delivery</p>
                </div>
              </div>
            </div>

            <div className="relative animate-fade-in-delay-2">
              <div className="absolute inset-0 bg-[#6BB5E0]/20 blur-3xl rounded-full"></div>
              <img 
                src={LOGO_URL} 
                alt="GasMan4U Delivery" 
                className="relative z-10 w-full max-w-md mx-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-[#f8fafc]" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Our <span className="text-[#0284c7]">Services</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              We provide a complete range of gas and water delivery services to meet all your needs.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Gas Cylinders */}
            <div className="service-card group" data-testid="service-gas-cylinders">
              <div className="w-14 h-14 bg-[#6BB5E0]/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#6BB5E0]/20 transition-colors">
                <Flame className="text-[#0284c7]" size={28} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Gas Cylinders</h3>
              <p className="text-gray-600 mb-4">
                Premium quality LPG cylinders for homes and businesses. Safe, efficient, and delivered on time.
              </p>
              <div className="flex items-center text-[#0284c7] text-sm font-semibold">
                Learn more <ChevronRight size={16} className="ml-1" />
              </div>
            </div>

            {/* Water Cans */}
            <div className="service-card group" data-testid="service-water-cans">
              <div className="w-14 h-14 bg-[#6BB5E0]/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#6BB5E0]/20 transition-colors">
                <Droplets className="text-[#0284c7]" size={28} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Water Cans</h3>
              <p className="text-gray-600 mb-4">
                Pure and clean drinking water delivered in sealed cans. Stay hydrated with our quality water.
              </p>
              <div className="flex items-center text-[#0284c7] text-sm font-semibold">
                Learn more <ChevronRight size={16} className="ml-1" />
              </div>
            </div>

            {/* Regulators & Accessories */}
            <div className="service-card group" data-testid="service-regulators">
              <div className="w-14 h-14 bg-[#6BB5E0]/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#6BB5E0]/20 transition-colors">
                <Settings className="text-[#0284c7]" size={28} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Regulators & Accessories</h3>
              <p className="text-gray-600 mb-4">
                High-quality gas regulators, pipes, and accessories. Safety equipment for your peace of mind.
              </p>
              <div className="flex items-center text-[#0284c7] text-sm font-semibold">
                Learn more <ChevronRight size={16} className="ml-1" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-white" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                Why Choose <span className="text-[#0284c7]">GasMan4U</span>?
              </h2>
              <p className="text-gray-600 mb-8">
                With years of experience in the gas delivery industry, we've built a reputation for 
                reliability, safety, and excellent customer service. Our team is dedicated to providing 
                you with the best products at competitive prices.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#6BB5E0]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="text-[#0284c7]" size={24} />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Quick Delivery</h4>
                    <p className="text-gray-600 text-sm">Average delivery time of 30 minutes within city limits.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#6BB5E0]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Shield className="text-[#0284c7]" size={24} />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Safety First</h4>
                    <p className="text-gray-600 text-sm">All products meet safety standards and are regularly inspected.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#6BB5E0]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Truck className="text-[#0284c7]" size={24} />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Wide Coverage</h4>
                    <p className="text-gray-600 text-sm">Serving multiple areas with our extensive store location network.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 bg-[#6BB5E0]/10 blur-3xl rounded-full"></div>
              <div className="relative bg-white border border-gray-200 rounded-2xl p-8 shadow-lg">
                <img 
                  src={LOGO_URL} 
                  alt="About GasMan4U" 
                  className="w-full max-w-xs mx-auto mb-6"
                />
                <div className="text-center">
                  <p className="text-2xl font-bold text-[#0284c7] mb-2">Trusted Since 2020</p>
                  <p className="text-gray-600">Serving thousands of happy customers</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-[#f8fafc]" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Get in <span className="text-[#0284c7]">Touch</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Have questions or need to place an order? Contact us through any of the following channels.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white border border-gray-200 rounded-xl p-8 text-center hover:border-[#6BB5E0] hover:shadow-lg transition-all" data-testid="contact-phone">
              <div className="w-14 h-14 bg-[#6BB5E0]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Phone className="text-[#0284c7]" size={24} />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Phone</h3>
              <p className="text-gray-600">+91 98765 43210</p>
              <p className="text-gray-600">+91 98765 43211</p>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-8 text-center hover:border-[#6BB5E0] hover:shadow-lg transition-all" data-testid="contact-email">
              <div className="w-14 h-14 bg-[#6BB5E0]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="text-[#0284c7]" size={24} />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Email</h3>
              <p className="text-gray-600">info@gasman4u.com</p>
              <p className="text-gray-600">support@gasman4u.com</p>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-8 text-center hover:border-[#6BB5E0] hover:shadow-lg transition-all" data-testid="contact-address">
              <div className="w-14 h-14 bg-[#6BB5E0]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPin className="text-[#0284c7]" size={24} />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Address</h3>
              <p className="text-gray-600">123 Gas Street, Industrial Area</p>
              <p className="text-gray-600">Mumbai, Maharashtra 400001</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="GasMan4U" className="h-8 w-auto" />
              <span className="font-bold text-gray-900">GasMan4U</span>
            </div>
            <p className="text-gray-500 text-sm">
              © 2026 GasMan4U. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
